import React, { createContext, useContext, useMemo } from 'react';
import {
	ENABLE_ARCHIVE_DELETED_VIEWS,
	ENABLE_REVIEW_SHORTCUT_HINTS,
	ENABLE_SCHEDULE_FACET_FILTERS,
	ENABLE_SCHEDULE_TASK_NOTES,
	ENABLE_SCHEDULE_WORKSPACE_VIEW,
} from '@/config/featureFlags';

/**
 * Feature flags as ambient context, read where they are used.
 *
 * The alternative is threading a flag from the route down to the surface that reads it, with every
 * layer in between carrying a prop it never looks at and relaying `undefined` to a default. A flag
 * is ambient configuration, not per-instance behaviour; it travels as context.
 *
 * The rules that keep this from becoming its own smell:
 *
 * - **No provider in the app.** The context default *is* the env configuration, so production reads
 *   flags with zero providers mounted and zero drilling.
 * - **The provider is the override point** — for a story toggling a flag, or a test pinning one.
 *   Mount it once, at the surface being exercised, never nested.
 * - **A component may still take a prop for the same behaviour** when a caller genuinely varies it
 *   per instance; the flag is then only that prop's default. Story arg toggles keep working.
 */
export interface FeatureFlags {
	/** Shows the not-yet-connected deleted task, contact and record archive views. */
	archiveDeletedViews: boolean;
	/** Offers the schedule's in-progress Workspace view on the view toggle. */
	scheduleWorkspaceView: boolean;
	/** Offers the facet filters beside the schedule's lenses. */
	scheduleFacetFilters: boolean;
	/** Draws the keyboard legend under a proposal's verdicts. The keys work either way. */
	reviewShortcutHints: boolean;
	/** Offers Notes as its own action in the task drawer's overflow menu. */
	scheduleTaskNotes: boolean;
}

const ENV_FLAGS: FeatureFlags = {
	archiveDeletedViews: ENABLE_ARCHIVE_DELETED_VIEWS,
	scheduleWorkspaceView: ENABLE_SCHEDULE_WORKSPACE_VIEW,
	scheduleFacetFilters: ENABLE_SCHEDULE_FACET_FILTERS,
	reviewShortcutHints: ENABLE_REVIEW_SHORTCUT_HINTS,
	scheduleTaskNotes: ENABLE_SCHEDULE_TASK_NOTES,
};

const FeatureFlagsContext = createContext<FeatureFlags>(ENV_FLAGS);

export const FeatureFlagsProvider: React.FC<{ flags?: Partial<FeatureFlags>; children: React.ReactNode }> = ({
	flags,
	children,
}) => {
	const value = useMemo(() => ({ ...ENV_FLAGS, ...flags }), [flags]);
	return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlags = (): FeatureFlags => useContext(FeatureFlagsContext);
